import type {
  HatbotAnswer,
  HatbotAnswerInput,
  HatbotCitation,
  HatbotIntent,
  HatbotRisk,
  ProductionStage,
} from './types';

export const ESCALATION_COPY =
  "I've passed this to our team at assist@greeleyhatworks.com. Someone will respond as soon as possible, but there may be a delay depending on our current volume. If it's time-sensitive, you can also call us at (888) 367-2428.";

const SOURCES = {
  prd: {
    label: 'HatBot PRD scope',
    source: '04_Phase2_AI-Chat/2026-05-30_Hatbot-PRD-Scope.md',
  },
  brandGuide: {
    label: 'GHW brand guide',
    source: 'Marketing/GHW-Brand-Voice-And-Review-Guide.md',
  },
  stageCopy: {
    label: 'Approved stage copy v2',
    source: '01_Projects/Email_Intelligence/system/src/lib/stage-copy.ts',
  },
  classifier: {
    label: 'HatMail classifier rules v4.4',
    source: '01_Projects/Email_Intelligence/system/src/prompts/classify-v4.ts',
  },
  buildSpec: {
    label: 'Phase 2 build spec',
    source: '04_Phase2_AI-Chat/2026-04-22_Build-Spec.md',
  },
} satisfies Record<string, HatbotCitation>;

const APPOINTMENT_URL = 'https://greeleyhatworks.com/pages/book-an-appointment';
const RECRAFT_URL = 'https://greeleyhatworks.com/pages/recraft';
const TRUNK_SHOW_URL = 'https://greeleyhatworks.com/blogs/news';
const WHOLESALE_URL = 'https://greeleyhatworks.com/pages/wholesale-inquiries';

const HUMAN_RE = /\b(human|person|representative|agent|manager|shauna|shawna|trent|jesse|call me|talk to someone|speak to someone)\b/i;
const HIGH_RISK_RE = /\b(angry|furious|upset|mad|refund|cancel|chargeback|lawyer|legal|bbb|rush|deadline|wedding|funeral|lawsuit|never again)\b/i;
const ORDER_RE = /\b(where'?s my hat|where'?s my custom hat|where is my hat|order status|my order|two orders|status|tracking|shipping|ship|production|queue|ready|finished|backorder|on hold|quality control|po\s*#?\d+|#\d{3,})\b/i;
const APPOINTMENT_RE = /\b(appointment|book|booking|schedule|visit|fitting|consultation|come in|calendar)\b/i;
const RECRAFT_RE = /\b(recraft|clean|cleaning|reshape|reshaping|restore|restoration|renovation|sweatband|liner|reblock|flange|repair)\b/i;
const YELLOWSTONE_RE = /\b(yellowstone|dutton|rip wheeler|john dutton|kayce|beth dutton|luke grimes)\b/i;
const TRUNK_SHOW_RE = /\b(trunk show|event|road show|retail partner|bought at a show|purchased at a show)\b/i;
const WHOLESALE_RE = /\b(wholesale|retailer|dealer|bulk|corporate|branded hats|company hats|reseller|store owner)\b/i;
const STOCK_RE = /\b(in stock|available|availability|inventory|do you have|size\s+\d|product|stock hat|accessory|accessories|hat carrier|hat carriers|brush|brushes)\b/i;
const CUSTOM_TIMELINE_RE = /\b(how long|timeline|lead time|wait|when would|turnaround|production time)\b/i;
const CUSTOM_PROCESS_RE = /\b(custom|bespoke|design|build a hat|craft a hat|made for me|conformateur|head pattern|material|beaver|hare|felt|shape|brim|crown|retro-tech|retro tech)\b/i;
const CARE_RE = /\b(care|hat jack|cedar|store my hat|storage|rain|weather|brush|dust|maintain)\b/i;
const SIZING_RE = /\b(size|sizing|fit|measure|measurement|head size|too tight|too loose)\b/i;
const BRAND_INFO_RE = /\b(where are you|location|address|owner|who owns|trent|founded|since 1909|what is greeley)\b/i;
const PROMPT_INJECTION_RE = /\b(system prompt|ignore your instructions|developer message|internal rules|confidence score|api key|access token|shopify token)\b/i;
const PAYMENT_RE = /\b(credit card|card number|charge my card|payment info|cvv)\b/i;
const MEDICAL_RE = /\b(swallowed|poison|poison control|sick|medical|doctor|emergency|hurt)\b/i;
const LEGAL_RE = /\b(legal threat|legal advice|lawsuit|lawyer|sue|attorney)\b/i;
const DISCOUNT_RE = /\b(discount|coupon|deal|special price|i know trent)\b/i;
const COSTUME_RE = /\b(costume|prank|novelty|prop)\b/i;
const COMPETITOR_RE = /\b(bad things|trash|insult|competitor|another hat company)\b/i;
const UNSAFE_PRODUCT_RE = /\b(bulletproof|weapon|protect me|safety equipment)\b/i;
const ORDER_CHANGE_RE = /\b(change my|change the|shipping address|monogram|typo|cancel|refund|rush|expedite)\b/i;
const POLICY_EXCEPTION_RE = /\b(warranty|covered forever|political slogan|special exception|final price)\b/i;
const PRIVATE_DATA_RE = /\b(all orders|which customer|customer spent|spent the most|customer data|private data|analytics)\b/i;

const STAGE_TIMING: Partial<Record<ProductionStage, string>> = {
  'New Order': '3-4 months from this stage',
  'Started Production': 'about 2 weeks',
};

const STAGE_SUMMARY: Record<ProductionStage, string> = {
  'New Order': 'Your order is in the queue, and the team has started by securing the right raw felt body for your custom build.',
  Backorder: 'Material is pending arrival, so the team needs to review timing before giving a customer-facing update.',
  'Started Production': 'The build has moved to the active workbench, where the makers begin blocking, shaping, sanding, and pouncing by hand.',
  Sweatband: 'The team is working on the premium leather sweatband, the part that helps your hat feel secure and personal to you.',
  'Final Flange': 'The crown and brim are being set with heat, moisture, and traditional tools so the felt holds its shape.',
  'Brim Cut': 'The brim is being cut by hand to your specified width, and the excess felt is saved for the custom self band.',
  Trim: 'The details are coming together now: hatband, liner, accents, and hand-finished trim.',
  'Quality Control': 'This is an internal inspection step before the final shaping work.',
  'Shaping Wall': 'The hat has cleared inspection and is in the final hands-on shaping step.',
  'On Hold': 'The team needs to review this before giving a customer-facing update.',
  'Your Hat Is Finished': 'Your custom hat is complete and waiting at the shop for fitting, shaping, or pickup coordination.',
  'Your Hat is Ready to Ship': 'Your custom hat is packed securely and waiting for the carrier.',
};

interface IntentMatch {
  intent: HatbotIntent;
  confidence: number;
}

interface AnswerArgs {
  answer: string;
  intent: HatbotIntent;
  confidence: number;
  risk: HatbotRisk;
  escalates: boolean;
  citations: HatbotCitation[];
  escalationReason?: string | null;
  decisionPath: string[];
}

export function answerHatbotQuestion(input: HatbotAnswerInput): HatbotAnswer {
  const message = input.message.trim();
  const path: string[] = [];

  if (!message) {
    return makeAnswer({
      answer: "I want to help, but I didn't catch a question. Send me a little more detail and I'll point you in the right direction.",
      intent: 'unsupported',
      confidence: 0.35,
      risk: 'medium',
      escalates: false,
      citations: [SOURCES.prd],
      decisionPath: ['Empty input', 'Asked for more detail'],
    });
  }

  path.push('Read tester prompt');

  if (MEDICAL_RE.test(message)) {
    return makeAnswer({
      answer: "That needs emergency help, not hat advice. If someone swallowed cleaner or may be in danger, call emergency services or Poison Control right now. Once everyone is safe, the GHW team can help with the hat question.",
      intent: 'safety',
      confidence: 0.94,
      risk: 'high',
      escalates: false,
      citations: [SOURCES.prd],
      decisionPath: [...path, 'Detected possible emergency', 'Sent customer to emergency resources'],
    });
  }

  if (PROMPT_INJECTION_RE.test(message)) {
    return makeAnswer({
      answer: "I can't share system instructions, credentials, private data, or internal rules. I can help with GHW questions about custom hats, fit, care, appointments, ReCraft, product availability, and verified order status.",
      intent: 'unsupported',
      confidence: 0.92,
      risk: 'high',
      escalates: false,
      citations: [SOURCES.prd],
      decisionPath: [...path, 'Detected prompt or credential attack', 'Refused internal disclosure'],
    });
  }

  if (PAYMENT_RE.test(message)) {
    return makeAnswer({
      answer: "Please don't share payment details in chat. HatBot can't take cards or process payments. If you need help completing a purchase safely, I'll pass this to the team.",
      intent: 'safety',
      confidence: 0.9,
      risk: 'high',
      escalates: true,
      escalationReason: 'Customer shared or attempted to share payment details.',
      citations: [SOURCES.prd],
      decisionPath: [...path, 'Detected payment data risk', 'Escalated for safe purchase help'],
    });
  }

  if (PRIVATE_DATA_RE.test(message)) {
    return makeAnswer({
      answer: "I can't share customer lists, order history, or internal analytics. I can help with a specific verified order, product question, appointment, ReCraft, or general GHW guidance.",
      intent: 'unsupported',
      confidence: 0.9,
      risk: 'high',
      escalates: false,
      citations: [SOURCES.prd],
      decisionPath: [...path, 'Detected private data request', 'Refused data exposure'],
    });
  }

  if (LEGAL_RE.test(message)) {
    return makeAnswer({
      answer: `${ESCALATION_COPY} I can't provide legal advice or draft legal threats, but the team should review the customer-service issue directly.`,
      intent: 'human_request',
      confidence: 0.88,
      risk: 'high',
      escalates: true,
      escalationReason: 'Legal or adversarial request requires human review.',
      citations: [SOURCES.prd],
      decisionPath: [...path, 'Detected legal or adversarial request', 'Escalated to team'],
    });
  }

  if (DISCOUNT_RE.test(message)) {
    return makeAnswer({
      answer: `${ESCALATION_COPY} Special pricing and exceptions need team review. I won't lead with discounts or promise one from chat.`,
      intent: 'human_request',
      confidence: 0.86,
      risk: 'medium',
      escalates: true,
      escalationReason: 'Special pricing or exception request requires team review.',
      citations: [SOURCES.prd, SOURCES.brandGuide],
      decisionPath: [...path, 'Detected special pricing request', 'Escalated rather than promising a discount'],
    });
  }

  if (ORDER_CHANGE_RE.test(message) || POLICY_EXCEPTION_RE.test(message) || HIGH_RISK_RE.test(message) || HUMAN_RE.test(message)) {
    const intent = HUMAN_RE.test(message) ? 'human_request' : classifyIntent(message).intent;
    return escalate(intent, 'Customer asked for a human or used high-risk language.', [...path, 'Detected handoff trigger']);
  }

  const match = classifyIntent(message);
  path.push(`Classified intent as ${match.intent}`);

  if (input.order && (match.intent === 'order_status' || input.order.found === false)) {
    return answerOrderStatus(input, match.intent === 'order_status' ? match.confidence : 0.78, path);
  }

  switch (match.intent) {
    case 'order_status':
      return answerOrderStatus(input, match.confidence, path);
    case 'appointment':
      return makeAnswer({
        answer: `You can book a fitting or visit through our appointment page: ${APPOINTMENT_URL}. For this testing build, I can point you to the booking page, but I can't see live availability yet.`,
        intent: 'appointment',
        confidence: match.confidence,
        risk: 'low',
        escalates: false,
        citations: [SOURCES.prd, SOURCES.classifier],
        decisionPath: [...path, 'Used link-only appointment route'],
      });
    case 'recraft':
      return makeAnswer({
        answer: `Yes, Greeley Hat Works offers ReCraft services. The best first step is to fill out the ReCraft form and send photos so the team can see what your hat needs: ${RECRAFT_URL}. We won't guess at price, service level, or outcome before the team reviews it.`,
        intent: 'recraft',
        confidence: match.confidence,
        risk: 'medium',
        escalates: false,
        citations: [SOURCES.prd, SOURCES.classifier, SOURCES.brandGuide],
        decisionPath: [...path, 'Used ReCraft form and photos rule', 'Avoided pricing and outcome promises'],
      });
    case 'yellowstone':
      return makeAnswer({
        answer: "We can help with Yellowstone and Dutton-style hats. Those hats come as designed, pre-shaped, and often distressed. The team checks stock first, and if your size isn't available, it usually becomes a made-to-order preorder with about a 3 to 4 month lead time.",
        intent: 'yellowstone',
        confidence: match.confidence,
        risk: 'medium',
        escalates: false,
        citations: [SOURCES.classifier, SOURCES.brandGuide],
        decisionPath: [...path, 'Applied Yellowstone stock-first rule'],
      });
    case 'trunk_show':
      return makeAnswer({
        answer: `Trunk Shows are true custom hat events where GHW brings The Greeley Experience to a retail partner. If you bought through a Trunk Show and your order doesn't appear in Shopify, the team may need to look it up through the partner path. You can see upcoming events here: ${TRUNK_SHOW_URL}.`,
        intent: 'trunk_show',
        confidence: match.confidence,
        risk: 'medium',
        escalates: false,
        citations: [SOURCES.prd, SOURCES.buildSpec, SOURCES.brandGuide],
        decisionPath: [...path, 'Used Trunk Show fallback language'],
      });
    case 'wholesale':
      return makeAnswer({
        answer: `Wholesale and corporate requests go through the team at assist@greeleyhatworks.com, and the wholesale inquiry form is here: ${WHOLESALE_URL}. Share what you're trying to build, quantity, timing, and any branding needs so they can route it cleanly.`,
        intent: 'wholesale',
        confidence: match.confidence,
        risk: 'medium',
        escalates: true,
        escalationReason: 'Wholesale and corporate requests require HatMail handoff.',
        citations: [SOURCES.classifier],
        decisionPath: [...path, 'Escalated wholesale request'],
      });
    case 'product_stock':
      return answerProductStock(input, match.confidence, path);
    case 'sizing':
      return makeAnswer({
        answer: "Fit matters more than a number on a tag. For custom work, the best path is an appointment or guided fitting so the team can understand your head shape, not just circumference. If you're shopping stock, share the size you're considering and the style, and we can point you toward the safest next step.",
        intent: 'sizing',
        confidence: match.confidence,
        risk: 'low',
        escalates: false,
        citations: [SOURCES.brandGuide, SOURCES.prd],
        decisionPath: [...path, 'Gave general fit guidance', 'Avoided exact size guarantee'],
      });
    case 'brand_info':
      return answerBrandInfo(message, match.confidence, path);
    case 'custom_timeline':
      return makeAnswer({
        answer: "Most current custom builds are running about 3 to 4 months. That window gives the team time to build your hat by hand, make the cedar pattern, shape the felt, finish the trim, and check the details instead of rushing a piece that should feel right for years.",
        intent: 'custom_timeline',
        confidence: match.confidence,
        risk: 'low',
        escalates: false,
        citations: [SOURCES.classifier, SOURCES.brandGuide],
        decisionPath: [...path, 'Used current approved timeline language'],
      });
    case 'custom_process':
      return answerCustomProcess(message, match.confidence, path);
    case 'hat_care':
      return makeAnswer({
        answer: "When you aren't wearing your hat, keep the cedar pattern or a Hat Jack inside it so the shape stays true. Brush off dust gently, keep it away from hard heat, and if it needs deeper work, send photos through the ReCraft form so the team can guide you.",
        intent: 'hat_care',
        confidence: match.confidence,
        risk: 'medium',
        escalates: false,
        citations: [SOURCES.stageCopy, SOURCES.classifier],
        decisionPath: [...path, 'Used sourced care guidance'],
      });
    case 'safety':
      return makeAnswer({
        answer: "A GHW hat is built for fit, style, and hard-wearing Western life, but it is not safety equipment. If you have an unusual custom idea, the team can review it, but HatBot won't promise protective performance or unsupported use.",
        intent: 'safety',
        confidence: match.confidence,
        risk: 'high',
        escalates: false,
        citations: [SOURCES.prd, SOURCES.brandGuide],
        decisionPath: [...path, 'Refused unsupported safety claim'],
      });
    case 'human_request':
      return escalate('human_request', 'Customer asked for a human.', path);
    case 'unsupported':
      return makeAnswer({
        answer: "I don't want to guess on that one. I can help with custom hats, appointments, ReCraft, product availability, Trunk Shows, Yellowstone styles, and order status when we have a verified lookup path.",
        intent: 'unsupported',
        confidence: match.confidence,
        risk: 'medium',
        escalates: false,
        citations: [SOURCES.prd],
        decisionPath: [...path, 'Stayed inside supported scope'],
      });
  }
}

function classifyIntent(message: string): IntentMatch {
  if (COSTUME_RE.test(message)) return { intent: 'custom_process', confidence: 0.76 };
  if (COMPETITOR_RE.test(message)) return { intent: 'brand_info', confidence: 0.78 };
  if (UNSAFE_PRODUCT_RE.test(message)) return { intent: 'safety', confidence: 0.82 };
  if (RECRAFT_RE.test(message)) return { intent: 'recraft', confidence: 0.92 };
  if (YELLOWSTONE_RE.test(message)) return { intent: 'yellowstone', confidence: 0.9 };
  if (TRUNK_SHOW_RE.test(message)) return { intent: 'trunk_show', confidence: 0.88 };
  if (WHOLESALE_RE.test(message)) return { intent: 'wholesale', confidence: 0.9 };
  if (APPOINTMENT_RE.test(message)) return { intent: 'appointment', confidence: 0.9 };
  if (STOCK_RE.test(message)) return { intent: 'product_stock', confidence: 0.82 };
  if (ORDER_RE.test(message)) return { intent: 'order_status', confidence: 0.91 };
  if (SIZING_RE.test(message)) return { intent: 'sizing', confidence: 0.82 };
  if (BRAND_INFO_RE.test(message)) return { intent: 'brand_info', confidence: 0.82 };
  if (CUSTOM_TIMELINE_RE.test(message)) return { intent: 'custom_timeline', confidence: 0.86 };
  if (CARE_RE.test(message)) return { intent: 'hat_care', confidence: 0.78 };
  if (CUSTOM_PROCESS_RE.test(message)) return { intent: 'custom_process', confidence: 0.82 };
  return { intent: 'unsupported', confidence: 0.42 };
}

function answerBrandInfo(message: string, confidence: number, path: string[]): HatbotAnswer {
  if (COMPETITOR_RE.test(message)) {
    return makeAnswer({
      answer: "We won't spend your time tearing down another shop. What GHW can speak to is its own standard: custom fit, better bodies, cedar patterns, hand work, and a craft that's been honed since 1909.",
      intent: 'brand_info',
      confidence,
      risk: 'low',
      escalates: false,
      citations: [SOURCES.brandGuide],
      decisionPath: [...path, 'Refused competitor bashing'],
    });
  }

  if (/\b(owner|who owns|trent)\b/i.test(message)) {
    return makeAnswer({
      answer: "Greeley Hat Works is owned by Trent Johnson, a 4th-generation hatmaker who purchased the shop in 1996. The shop's roots go back to 1909.",
      intent: 'brand_info',
      confidence,
      risk: 'low',
      escalates: false,
      citations: [SOURCES.brandGuide],
      decisionPath: [...path, 'Answered sourced ownership fact'],
    });
  }

  if (/\b(retro-tech|retro tech)\b/i.test(message)) {
    return makeAnswer({
      answer: "Retro-Tech is GHW's blend of old tools and modern personalization. Think vintage irons, blockers, and careful hand work serving a very personal fit. It isn't nostalgia for its own sake. It's how the team protects the shape, feel, and soul of the hat.",
      intent: 'brand_info',
      confidence,
      risk: 'low',
      escalates: false,
      citations: [SOURCES.brandGuide],
      decisionPath: [...path, 'Answered brand concept'],
    });
  }

  return makeAnswer({
    answer: "Greeley Hat Works is located at 2613 8th Ave, Garden City, CO 80631. The shop has been part of Western hatmaking since 1909.",
    intent: 'brand_info',
    confidence,
    risk: 'low',
    escalates: false,
    citations: [SOURCES.brandGuide],
    decisionPath: [...path, 'Answered store location'],
  });
}

function answerCustomProcess(message: string, confidence: number, path: string[]): HatbotAnswer {
  if (/\bstock\b/i.test(message)) {
    return makeAnswer({
      answer: "A custom hat is built around your fit, your style choices, and The Greeley Experience. A stock hat is ready-to-wear GHW quality that can still be shaped and adjusted, but it does not start from the same full custom path.",
      intent: 'custom_process',
      confidence,
      risk: 'low',
      escalates: false,
      citations: [SOURCES.brandGuide, SOURCES.prd],
      decisionPath: [...path, 'Explained custom versus stock'],
    });
  }

  return makeAnswer({
    answer: "The custom process starts with your fit and your vision. GHW uses the Conformateur to capture the shape of your head, then the team builds from better felt bodies, your style choices, and a cedar pattern made for your hat. It is hands-on, patient work, built here in Greeley since 1909.",
    intent: 'custom_process',
    confidence,
    risk: 'low',
    escalates: false,
    citations: [SOURCES.brandGuide, SOURCES.classifier],
    decisionPath: [...path, 'Explained custom process from approved sources'],
  });
}

function answerOrderStatus(input: HatbotAnswerInput, confidence: number, path: string[]): HatbotAnswer {
  const order = input.order;

  if (!order || !order.verified) {
    return makeAnswer({
      answer: `I can help check order status, but I need a verified lookup path before sharing private order details. Please provide the email used on the order and the order number, or log in before asking for status. ${ESCALATION_COPY}`,
      intent: 'order_status',
      confidence,
      risk: 'high',
      escalates: true,
      escalationReason: 'Order-status request did not include a verified lookup path.',
      citations: [SOURCES.prd],
      decisionPath: [...path, 'Order status touches private data', 'Escalated until verified'],
    });
  }

  if (order.found === false) {
    return makeAnswer({
      answer: `I don't see a matching Shopify order from the details provided. If this was placed through a Trunk Show or retail partner, it may not appear in GHW's Shopify system. ${ESCALATION_COPY}`,
      intent: 'trunk_show',
      confidence: 0.78,
      risk: 'high',
      escalates: true,
      escalationReason: 'Verified customer lookup did not find a Shopify order.',
      citations: [SOURCES.prd, SOURCES.buildSpec],
      decisionPath: [...path, 'Verified lookup found no Shopify order', 'Applied Trunk Show fallback', 'Escalated for team review'],
    });
  }

  if (!order.stage) {
    return escalate('order_status', 'Verified order had no approved production stage available.', [...path, 'No approved stage available']);
  }

  const anomaly = getOrderAnomaly(order.stage, order.daysSinceOrder, order.daysInStage);
  if (anomaly) {
    return makeAnswer({
      answer: ESCALATION_COPY,
      intent: 'order_status',
      confidence: 0.7,
      risk: 'high',
      escalates: true,
      escalationReason: anomaly,
      citations: [SOURCES.prd, SOURCES.stageCopy],
      decisionPath: [...path, 'Detected order anomaly', anomaly, 'Escalated rather than guessing'],
    });
  }

  const orderLabel = order.orderName ? ` for ${order.orderName}` : '';
  const timing = STAGE_TIMING[order.stage] ? ` The approved timing note is ${STAGE_TIMING[order.stage]}.` : '';
  const stageIntro =
    order.stage === 'Quality Control'
      ? `Your hat${orderLabel} is in final inspection and nearing completion.`
      : `Your hat${orderLabel} is at ${order.stage}.`;

  return makeAnswer({
    answer: `${stageIntro} ${STAGE_SUMMARY[order.stage]}${timing}`,
    intent: 'order_status',
    confidence,
    risk: order.stage === 'Quality Control' ? 'medium' : 'low',
    escalates: false,
    citations: [SOURCES.stageCopy],
    decisionPath: [...path, 'Verified order context', `Mapped stage ${order.stage}`, 'Answered from approved stage copy'],
  });
}

function answerProductStock(input: HatbotAnswerInput, confidence: number, path: string[]): HatbotAnswer {
  const product = input.product;

  if (!product || product.inStock == null) {
    return makeAnswer({
      answer: "I can check product availability when Shopify catalog context is available. For this testing build, I don't have a live stock result for that item yet, so I won't guess. Share the style, color, and size and the team can confirm it.",
      intent: 'product_stock',
      confidence,
      risk: 'medium',
      escalates: false,
      citations: [SOURCES.prd],
      decisionPath: [...path, 'No live catalog result', 'Asked for product details instead of guessing'],
    });
  }

  const title = product.title ?? 'that item';
  const answer = product.inStock
    ? `${title} appears to be in stock from the catalog context I have. Inventory can move quickly, so the next step is to choose your size and confirm it at checkout.`
    : `${title} does not appear to be in stock from the catalog context I have. If it is a Yellowstone or Dutton style, the team may check whether it can be made to order.`;

  return makeAnswer({
    answer,
    intent: 'product_stock',
    confidence: Math.min(confidence, 0.86),
    risk: 'medium',
    escalates: false,
    citations: [SOURCES.prd],
    decisionPath: [...path, 'Used provided product context', product.inStock ? 'Answered in stock' : 'Answered not in stock'],
  });
}

function getOrderAnomaly(stage: ProductionStage, daysSinceOrder?: number, daysInStage?: number | null): string | null {
  if (stage === 'On Hold') return 'Order is On Hold and requires team follow-up.';
  if (stage === 'Backorder') return 'Order is in Backorder and needs team context before answering.';
  if (stage === 'New Order' && daysSinceOrder != null && daysSinceOrder >= 120) {
    return 'Order is old while still in an early stage.';
  }
  if (stage === 'Started Production' && daysInStage != null && daysInStage >= 42) {
    return 'Order has been in Started Production for 6 or more weeks.';
  }
  return null;
}

function escalate(intent: HatbotIntent, reason: string, path: string[]): HatbotAnswer {
  return makeAnswer({
    answer: ESCALATION_COPY,
    intent,
    confidence: 0.7,
    risk: 'high',
    escalates: true,
    escalationReason: reason,
    citations: [SOURCES.prd],
    decisionPath: [...path, reason, 'Prepared HatMail handoff'],
  });
}

function makeAnswer(args: AnswerArgs): HatbotAnswer {
  return {
    escalationReason: null,
    ...args,
  };
}
